import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    CircularProgress,
    Button,
    useTheme,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery } from '@tanstack/react-query';
import { fetchDeviceSummary } from '../services/api';
import { DeviceSummary } from '../types/api';
import { exportToCSV } from '../utils/csvExport';

// These are the fields visible in the table - used for default selection
const VISIBLE_FIELDS = [
    'SiteName',
    'PulsewayGroupName',
    'TotalDevices',
    'TotalWindows10Devices',
    'TotalWindows11Devices',
    'TotalLaptopCount',
    'HardwareReadinessCapable',
    'HardwareReadinessNotCapable',
    'HardwareReadinessUndetermined',
    'NewestDeviceUpdate',
    'OldestDeviceUpdate',
    'TotalMissingPulseway',
    'TotalMissingSophos',
    'TotalMissingThreatLocker'
];

export const DeviceSummaryTable: React.FC = () => {
    const [isExporting, setIsExporting] = useState(false);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState<string[]>(VISIBLE_FIELDS);
    const [availableFields, setAvailableFields] = useState<string[]>([]);
    const theme = useTheme();

    const { data: summaryData, isLoading, error, refetch } = useQuery({
        queryKey: ['deviceSummary'],
        queryFn: fetchDeviceSummary,
        staleTime: 5000,
    });

    // Update availableFields when data is loaded
    useEffect(() => {
        if (summaryData?.data.data && summaryData.data.data.length > 0) {
            setAvailableFields(Object.keys(summaryData.data.data[0]).sort());
        }
    }, [summaryData]);

    const handleExportDialogOpen = () => {
        // Reset selected fields to visible fields when opening dialog
        setSelectedFields(VISIBLE_FIELDS);
        setIsExportDialogOpen(true);
    };

    const handleExportDialogClose = () => {
        setIsExportDialogOpen(false);
    };

    const handleFieldToggle = (field: string) => {
        setSelectedFields(prev => 
            prev.includes(field)
                ? prev.filter(f => f !== field)
                : [...prev, field]
        );
    };

    const handleSelectAll = () => {
        setSelectedFields(availableFields);
    };

    const handleDeselectAll = () => {
        setSelectedFields([]);
    };

    const handleExportCSV = async () => {
        try {
            setIsExporting(true);
            if (summaryData?.data.data) {
                exportToCSV(
                    summaryData.data.data, 
                    `device_summary_export_${new Date().toISOString().split('T')[0]}.csv`,
                    { fields: selectedFields }
                );
            }
        } catch (error) {
            console.error('Error exporting data:', error);
        } finally {
            setIsExporting(false);
            setIsExportDialogOpen(false);
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
                <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Paper 
            sx={{ 
                width: '100%', 
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box p={2} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                >
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportDialogOpen}
                    disabled={isExporting}
                >
                    Export to CSV
                </Button>
            </Box>

            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {VISIBLE_FIELDS.map((field) => (
                                <TableCell key={field} sx={{ backgroundColor: theme.palette.background.paper }}>
                                    {field}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summaryData?.data.data.map((summary: DeviceSummary, index: number) => (
                            <TableRow 
                                key={`${summary.SiteName}-${summary.PulsewayGroupName}-${index}`}
                                sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}
                            >
                                {VISIBLE_FIELDS.map((field) => (
                                    <TableCell key={field}>
                                        {field.includes('Update') 
                                            ? new Date(summary[field as keyof DeviceSummary] as string).toLocaleString()
                                            : summary[field as keyof DeviceSummary]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog 
                open={isExportDialogOpen} 
                onClose={handleExportDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Select Fields to Export</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                            Select All
                        </Button>
                        <Button size="small" onClick={handleDeselectAll}>
                            Deselect All
                        </Button>
                    </Box>
                    <FormGroup>
                        {availableFields.map((field) => (
                            <FormControlLabel
                                key={field}
                                control={
                                    <Checkbox
                                        checked={selectedFields.includes(field)}
                                        onChange={() => handleFieldToggle(field)}
                                    />
                                }
                                label={field}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleExportDialogClose} variant="outlined" color="primary">Cancel</Button>
                    <Button 
                        onClick={handleExportCSV}
                        variant="contained" 
                        color="primary"
                        disabled={selectedFields.length === 0 || isExporting}
                    >
                        {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}; 